/**
 * Blocklist matching for customer phone numbers (MDNs).
 *
 * Partner config stores these entries with the US country code (`+18006695426`, prefix `+1800`),
 * but the values we check against them are not consistently formatted: a voice task MDN can arrive
 * bare, and `removeCountryCode` strips a leading `+1` before the number is dispatched. Comparing
 * the two forms directly silently never matches, so both sides are reduced to digits and compared
 * with and without the leading country code.
 */

const toDigits = (value: string) => value.replace(/\D/g, "");

/** US 10-digit numbers are compared as `1XXXXXXXXXX` so `+1`-prefixed config entries line up. */
const withCountryCode = (digits: string) => (digits.length === 10 ? `1${digits}` : digits);

export const isBlocklistedNumber = (
    mdn: string,
    blocklistedNumbers?: string[],
    blocklistedPrefixes?: string[],
): boolean => {
    if (!mdn) return false;
    if (!blocklistedNumbers?.length && !blocklistedPrefixes?.length) return false;

    const digits = toDigits(mdn);
    if (!digits) return false;
    const prefixed = withCountryCode(digits);

    const numberMatches =
        blocklistedNumbers?.some((entry) => {
            const entryDigits = withCountryCode(toDigits(entry));
            return entryDigits.length > 0 && entryDigits === prefixed;
        }) ?? false;
    if (numberMatches) return true;

    // Prefixes are matched against both forms so an entry stored either way ("+1800" or "800") hits.
    return (
        blocklistedPrefixes?.some((prefix) => {
            const prefixDigits = toDigits(prefix);
            return prefixDigits.length > 0 && (prefixed.startsWith(prefixDigits) || digits.startsWith(prefixDigits));
        }) ?? false
    );
};
